import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type Phase = 'idle' | 'cliTyping' | 'cliOpening' | 'editorMounted' | 'editorGenerating' | 'done';

const TYPE_INTERVAL_MS = 22;
const ENTER_PAUSE_MS = 320;
const MOUNT_DELAY_MS = 700;
// Sync points inside editor-demo.mp4 (42s loop). Generation begins at 27s
// and finishes at 31s. The timeupdate listener maps currentTime → phase so
// the terminal narrative re-syncs on every video loop.
const VIDEO_GEN_START_S = 27;
const VIDEO_GEN_END_S = 31;

function prefersReducedMotion(): boolean {
  if (typeof matchMedia === 'undefined') return false;
  return matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Edition workflow demo. The fake browser plays a real screen recording of
 * the mask editor (mask painting, generation, result) — replacing the prior
 * simulated SVG strokes + cursor + screenshot stack because keyframe-driven
 * fakery can't match what the actual editor does. The terminal on the left
 * still narrates the CLI side of the same flow.
 *
 * Rendered as a `<div>` so the landing-page landmark count stays stable.
 */
export function EditorDemoSection() {
  const { t } = useTranslation('landing');
  const cliPrompt = t('editorDemo.userPrompt');
  const reduceMotion = useMemo(prefersReducedMotion, []);

  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  const [phase, setPhase] = useState<Phase>(reduceMotion ? 'done' : 'idle');
  const [cliTyped, setCliTyped] = useState(reduceMotion ? cliPrompt.length : 0);

  // Intersection trigger — only mount the video and start the terminal
  // animation once the section actually scrolls into view, so the 1.2 MB
  // video doesn't load on initial page hit.
  useEffect(() => {
    if (reduceMotion || started) return;
    const node = wrapRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(node);
    return () => {
      obs.disconnect();
    };
  }, [reduceMotion, started]);

  // Pre-video terminal narrative: cliTyping → cliOpening → editorMounted.
  // Once editorMounted fires we hand control to the video's timeupdate
  // listener (see effect below), which maps currentTime to editorGenerating
  // (27–31s) and done (≥31s) and naturally re-syncs on each loop.
  useEffect(() => {
    if (!started || reduceMotion) return;
    const timers: ReturnType<typeof setTimeout>[] = [];

    setPhase('cliTyping');
    for (let i = 1; i <= cliPrompt.length; i++) {
      timers.push(setTimeout(() => setCliTyped(i), i * TYPE_INTERVAL_MS));
    }
    let t0 = cliPrompt.length * TYPE_INTERVAL_MS + ENTER_PAUSE_MS;

    timers.push(setTimeout(() => setPhase('cliOpening'), t0));
    t0 += MOUNT_DELAY_MS;

    timers.push(
      setTimeout(() => {
        setPhase('editorMounted');
        // Start the video from t=0 in sync with editorMounted so the
        // 27s / 31s sync points are honored on the first playthrough.
        // 1.5× playback shortens the 42s recording to ~28s real time —
        // sync points stay in *video time* (currentTime), so the
        // timeupdate listener doesn't need any math change.
        const video = videoRef.current;
        if (video) {
          video.playbackRate = 1.5;
          try {
            video.currentTime = 0;
          } catch {
            /* some browsers reject seek before metadata is ready — ignore */
          }
          if (video.paused) void video.play().catch(() => undefined);
        }
      }, t0),
    );

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [started, reduceMotion, cliPrompt.length]);

  // Sync the terminal phase to the video's currentTime. Re-runs on every
  // timeupdate (~4–15Hz). Naturally handles loops: when the video wraps to
  // t≈0, phase falls back to editorMounted, the Generating/Saved lines
  // disappear, and the narrative replays.
  useEffect(() => {
    if (reduceMotion) return;
    const video = videoRef.current;
    if (!video) return;
    const handle = () => {
      const t = video.currentTime;
      setPhase((prev) => {
        // Pre-video phases are owned by the timer chain above — don't
        // override them while the user prompt is still typing.
        if (prev === 'idle' || prev === 'cliTyping' || prev === 'cliOpening') {
          return prev;
        }
        if (t >= VIDEO_GEN_END_S) return 'done';
        if (t >= VIDEO_GEN_START_S) return 'editorGenerating';
        return 'editorMounted';
      });
    };
    video.addEventListener('timeupdate', handle);
    return () => {
      video.removeEventListener('timeupdate', handle);
    };
  }, [reduceMotion, started]);

  // Once the user has scrolled past, pause the video to save CPU/network.
  // Cheap heuristic; not load-bearing, so wrap in IntersectionObserver too.
  useEffect(() => {
    if (reduceMotion) return;
    const node = wrapRef.current;
    const video = videoRef.current;
    if (!node || !video || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          if (started && video.paused) {
            video.playbackRate = 1.5;
            void video.play().catch(() => undefined);
          }
        } else {
          video.pause();
        }
      },
      { threshold: 0.15 },
    );
    obs.observe(node);
    return () => {
      obs.disconnect();
    };
  }, [reduceMotion, started]);

  const cliSlice = phase === 'cliTyping' ? cliPrompt.slice(0, cliTyped) : cliPrompt;
  const showCli = phase !== 'idle';
  const showMounting =
    phase === 'cliOpening' ||
    phase === 'editorMounted' ||
    phase === 'editorGenerating' ||
    phase === 'done';
  // Generating + Saved lines are driven by the video's timeupdate, so they
  // appear/disappear in sync with the on-screen generation (27s–31s in the
  // loop). They reset on every video loop.
  const showGenerating = phase === 'editorGenerating' || phase === 'done';
  const showCliDone = phase === 'done';
  const videoMounted = started || reduceMotion;
  const trailingCaret =
    phase === 'cliOpening' ||
    phase === 'editorMounted' ||
    phase === 'editorGenerating' ||
    phase === 'done';
  // During CLI typing + Mounting the terminal sits centered with the
  // editor pane hidden. The moment we transition to editorMounted (video
  // begins playing), the terminal slides off to the left and the editor
  // fades in to take centre stage. We don't touch the editor's z-20 vs
  // terminal's z-10 — the visibility gate is what lets the centered
  // terminal show through. Translate maths: terminal anchored at left-0,
  // 44% of parent width, +64% of element width = right edge at parent's
  // horizontal center.
  const terminalCentered = phase === 'idle' || phase === 'cliTyping' || phase === 'cliOpening';
  const editorReveal = !terminalCentered;

  return (
    <div className="mx-auto w-full max-w-[1280px] px-6 py-20" ref={wrapRef}>
      {/* Eyebrow + heading */}
      <div className="mx-auto max-w-3xl text-center">
        <span className="font-mono text-caption uppercase tracking-[0.25em] text-accent-cyan">
          {t('editorDemo.eyebrow')}
        </span>
        <h2 className="mt-3 text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.1] tracking-[-0.02em] text-primary">
          <span className="agb-shine-text">{t('editorDemo.heading')}</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-body text-secondary">
          {t('editorDemo.subheadline')}
        </p>
      </div>

      {/* Stage — terminal LEFT (behind), browser RIGHT (in front via z-index,
          overlapping the terminal's right edge). */}
      <div className="relative mx-auto mt-14 flex max-w-[1180px] flex-col gap-8 lg:block lg:min-h-[600px]">
        {/* ── Terminal (LEFT on desktop, behind) ──────────────────────── */}
        <div
          className={`order-2 transition-transform duration-700 ease-out lg:absolute lg:left-0 lg:top-[20%] lg:z-10 lg:w-[44%] ${
            terminalCentered ? 'lg:translate-x-[64%]' : 'lg:-translate-x-2'
          }`}
        >
          <div className="relative">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-px rounded-xl"
              style={{
                background:
                  'linear-gradient(140deg, rgba(221,252,116,0.4), rgba(45,225,252,0.2) 45%, transparent 75%)',
                padding: 1,
                WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              }}
            />
            <div className="relative overflow-hidden rounded-xl border border-white/[0.06] bg-surface shadow-[0_40px_120px_-30px_rgba(221,252,116,0.3)]">
              {/* Title bar — compact */}
              <div className="flex items-center gap-2 border-b border-white/[0.06] px-3.5 py-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-accent-red/80" role="presentation" />
                <div className="h-2.5 w-2.5 rounded-full bg-[#f5c542]/80" role="presentation" />
                <div className="h-2.5 w-2.5 rounded-full bg-accent-lime/80" role="presentation" />
                <span className="mx-auto -translate-x-4 font-mono text-[10px] text-muted">
                  <span className="sr-only">Terminal · </span>
                  {t('editorDemo.windowTitle')}
                </span>
              </div>

              <pre
                className="relative min-h-[340px] px-5 py-5 font-mono text-mono leading-relaxed sm:min-h-[380px]"
                aria-live="polite"
              >
                {/* Attached source — kept as a thumbnail so the input asset
                    is visible. We still ship editor-step-1.png in /public
                    just for this. */}
                <div className="not-italic">
                  <div className="mb-4 flex items-center gap-3 rounded-lg border border-accent-lime/25 bg-canvas/70 p-3">
                    <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-md bg-black">
                      <img
                        src="/editor-step-1.png"
                        alt={t('editorDemo.sourceAlt')}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <span className="flex flex-col gap-0.5 leading-tight">
                      <span className="font-mono text-caption uppercase tracking-[0.2em] text-accent-lime">
                        input
                      </span>
                      <span className="text-primary">{t('editorDemo.attachmentName')}</span>
                      <span className="text-caption text-muted">
                        {t('editorDemo.attachmentMeta')}
                      </span>
                    </span>
                  </div>
                </div>

                {/* CLI user prompt */}
                <div className="whitespace-pre-wrap text-primary">
                  <span className="text-accent-lime">{'> '}</span>
                  {showCli ? (
                    <span>{renderWithMention(cliSlice)}</span>
                  ) : (
                    <span className="text-muted">…</span>
                  )}
                  {phase === 'cliTyping' && <span className="agb-terminal-caret-lime" />}
                </div>

                {showMounting && (
                  <div className="mt-3 text-accent-cyan">{t('editorDemo.agentMounting')}</div>
                )}

                {showGenerating && (
                  <div className="mt-1 text-accent-cyan">
                    {t('editorDemo.agentMaskApplied')}{' '}
                    {phase === 'editorGenerating' && (
                      <span className="agb-terminal-dots" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                      </span>
                    )}
                  </div>
                )}

                {showCliDone && (
                  <>
                    <div className="mt-2 text-accent-lime">{t('editorDemo.agentDone')}</div>
                    <div className="text-secondary"> 1280×1024 · stage 2 · refined</div>
                  </>
                )}

                {trailingCaret && <span className="agb-terminal-caret-lime-static" />}
              </pre>
            </div>
          </div>
        </div>

        {/* ── Fake browser / editor video (RIGHT on desktop, in front) ── */}
        <div
          className={`order-1 transition-opacity duration-700 ease-out lg:relative lg:z-20 lg:ml-auto lg:w-[68%] ${
            editorReveal ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
          aria-hidden={!editorReveal}
        >
          <div className="relative">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-px rounded-xl"
              style={{
                background:
                  'linear-gradient(140deg, rgba(45,225,252,0.3), rgba(221,252,116,0.2) 50%, transparent 80%)',
                padding: 1,
                WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              }}
            />
            <div className="relative overflow-hidden rounded-xl border border-white/[0.06] bg-surface shadow-[0_40px_120px_-30px_rgba(45,225,252,0.3)]">
              {/* Browser chrome — thin */}
              <div className="flex items-center gap-2.5 border-b border-white/[0.06] bg-canvas/40 px-3 py-1.5">
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-accent-red/80" role="presentation" />
                  <div className="h-2.5 w-2.5 rounded-full bg-[#f5c542]/80" role="presentation" />
                  <div className="h-2.5 w-2.5 rounded-full bg-accent-lime/80" role="presentation" />
                </div>
                <div className="flex h-5 flex-1 items-center gap-1.5 rounded border border-white/[0.06] bg-canvas/70 px-2 font-mono text-[10px] tracking-tight text-secondary">
                  <span className="h-1 w-1 rounded-full bg-accent-lime/80" aria-hidden="true" />
                  {t('editorDemo.browserUrl')}
                </div>
              </div>

              {/* Editor canvas — real screen recording of the mask editor. */}
              <div className="relative w-full overflow-hidden bg-black">
                {videoMounted ? (
                  <video
                    ref={videoRef}
                    src="/editor-demo.mp4"
                    poster="/editor-step-1.png"
                    className="block h-auto w-full"
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-label={t('editorDemo.videoCaption')}
                  />
                ) : (
                  // Pre-trigger: render the poster as a static image so the
                  // section reserves the right height and there's no layout
                  // jump when the video swaps in.
                  <img
                    src="/editor-step-1.png"
                    alt={t('editorDemo.sourceAlt')}
                    className="block h-auto w-full"
                    loading="lazy"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes agb-terminal-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .agb-terminal-caret-lime,
        .agb-terminal-caret-lime-static {
          display: inline-block;
          width: 0.6ch;
          height: 1em;
          margin-left: 0.1em;
          vertical-align: -0.12em;
          background: var(--color-accent-lime);
          animation: agb-terminal-blink 1s step-end infinite;
        }
        @keyframes agb-terminal-dot {
          0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-2px); }
        }
        .agb-terminal-dots {
          display: inline-flex;
          gap: 4px;
          margin-left: 6px;
          vertical-align: middle;
        }
        .agb-terminal-dots span {
          width: 5px;
          height: 5px;
          border-radius: 9999px;
          background: var(--color-accent-cyan);
          display: inline-block;
          animation: agb-terminal-dot 1.1s ease-in-out infinite;
        }
        .agb-terminal-dots span:nth-child(2) { animation-delay: 0.18s; }
        .agb-terminal-dots span:nth-child(3) { animation-delay: 0.36s; }
        @media (prefers-reduced-motion: reduce) {
          .agb-terminal-caret-lime,
          .agb-terminal-caret-lime-static,
          .agb-terminal-dots span { animation: none; }
        }
      `}</style>
    </div>
  );
}

function renderWithMention(text: string) {
  const re = /@[\w./-]+/g;
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <span key={key++} className="text-accent-cyan">
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
